import { modelTypeArea, modelTypeBinaryClassifier, modelTypeTsm } from 'components/Pages/Jobs/inferenceFieldsOptions';
import {
  Input,
  Inputs,
  ResultsInput,
  SlideInferenceResults,
  SpotDetectionCountBin,
} from 'interfaces/calculateFeatures';
import { ClassificationModel } from 'interfaces/classificationModel';
import { ResultType } from 'interfaces/experimentResults';
import { ModelType } from 'interfaces/modelType';
import { getStainingMethodByStainType } from 'interfaces/stainType';
import {
  difference,
  find,
  findIndex,
  flatMap,
  forEach,
  groupBy,
  includes,
  isEmpty,
  map,
  some,
  uniq,
  values,
} from 'lodash';
import { AssignmentByStain } from 'utils/assignmentManagement';
import { getModelId, ModelPreferencesById } from 'utils/models';
import { OrchestrationBySlideByResultName, OrchestrationBySlideByType } from 'utils/orchestrationSelections';
import {
  BINARY_CLASSIFIER_DEFERRED_JOIN_MODE,
  isBinaryClassifierMultipleSelectionKey,
} from './binaryClassifierSelections';
import { OrchestrationMetadata } from './classConfigFromSelections';
import { applySpotDetectionSetupToInput, getSpotDetectionProbeNamesForStain } from './spotDetectionInputSetup';

export const buildCasesManifest = (params: {
  slides: SlideInferenceResults[];
  selectedOrchestrations: OrchestrationBySlideByType;
  allSelectedNonModelOrchestrations: OrchestrationBySlideByResultName[];
  selectedAssignments: AssignmentByStain;
}): Record<string, string[]> => {
  const { slides, selectedOrchestrations, allSelectedNonModelOrchestrations, selectedAssignments } = params;
  const casesManifest: Record<string, string[]> = {};
  const slidesByCase = groupBy(slides, 'caseId');
  const selectedAssignmentsValues = flatMap(values(selectedAssignments));

  forEach(slidesByCase, (slidesOfCase, caseId) => {
    const slideIdsToCheck = map(slidesOfCase, 'slideId');
    const slideIdsWithResults: string[] = [];

    forEach(slideIdsToCheck, (slideId) => {
      if (
        !isEmpty(selectedOrchestrations?.[slideId]) ||
        some(
          allSelectedNonModelOrchestrations,
          (nonModelOrchestrationsByResultName) => !isEmpty(nonModelOrchestrationsByResultName?.[slideId])
        ) ||
        some(selectedAssignmentsValues, (selectedAssignment) =>
          includes(selectedAssignment.assignment?.slideIds, slideId)
        )
      ) {
        slideIdsWithResults.push(slideId);
      }
    });

    if (!isEmpty(slideIdsWithResults)) {
      casesManifest[caseId] = slideIdsWithResults;
    }
  });

  return casesManifest;
};

export const buildInputsPerCasePerSlide = (params: {
  slides: SlideInferenceResults[];
  selectedOrchestrations: OrchestrationBySlideByType;
  allSelectedNonModelOrchestrations: OrchestrationBySlideByResultName[];
  selectedAssignments: AssignmentByStain;
  modelPreferencesByModelId: ModelPreferencesById;
  modelTypesByType: Record<string, ModelType[]>;
  classificationModelOptions: ClassificationModel[];
  casesManifest: Record<string, string[]>;
  orchestrationMetadata?: OrchestrationMetadata;
}): Inputs => {
  const {
    slides,
    selectedOrchestrations,
    allSelectedNonModelOrchestrations,
    selectedAssignments,
    modelPreferencesByModelId,
    modelTypesByType,
    classificationModelOptions,
    casesManifest,
    orchestrationMetadata,
  } = params;
  const inputsPerCasePerSlide: Inputs = {};
  const slidesByCase = groupBy(slides, 'caseId');
  const selectedAssignmentsValues = flatMap(values(selectedAssignments));
  const shouldTransferAreasByDefault = uniq(map(slides, 'stainingType')).length > 1;

  forEach(casesManifest, (slideIdsWithResults, caseId) => {
    const slidesOfCase = slidesByCase[caseId] || [];
    if (isEmpty(slideIdsWithResults)) {
      return;
    }

    inputsPerCasePerSlide[caseId] = {};

    forEach(slideIdsWithResults, (slideId) => {
      let modelTsmArtifactUrl = '';
      const slideStain = find(slidesOfCase, { slideId: slideId })?.stainingType;

      inputsPerCasePerSlide[caseId][slideId] = [];

      // Add selected orchestrations
      forEach(selectedOrchestrations?.[slideId], (orchestrationData, modelType) => {
        const isMultipleBinaryClassifierSelection =
          orchestrationData?.model?.modelType === modelTypeBinaryClassifier.apiModelValue &&
          isBinaryClassifierMultipleSelectionKey(modelType);
        const inputModelType = isMultipleBinaryClassifierSelection
          ? modelTypeBinaryClassifier.apiModelValue
          : modelType;
        const input: ResultsInput = {
          input: {
            artifactUrl: orchestrationData?.orchestration?.orchestrationResultArtifactUrlPattern,
            modelType: inputModelType,
          },
        };

        if (isMultipleBinaryClassifierSelection) {
          input.input.readAs = modelType;
          input.input.joinMode = BINARY_CLASSIFIER_DEFERRED_JOIN_MODE;
        }

        const modelId = orchestrationData?.model?.modelId ?? getModelId(orchestrationData?.model?.modelUrl);
        const modelUserPreferences = modelPreferencesByModelId?.[modelId];

        if (inputModelType === modelTypeArea.apiModelValue) {
          input.input.transferAreasToRegisteredStains =
            modelUserPreferences?.transferAreasToRegisteredStains ?? shouldTransferAreasByDefault;
        }

        if (modelUserPreferences && (modelUserPreferences?.classesToUse || modelUserPreferences?.classesToIgnore)) {
          if (modelUserPreferences?.useClassesFromIgnore) {
            input.input.classesToUse = difference(
              orchestrationData?.model?.classes,
              modelUserPreferences?.classesToIgnore
            );
          } else {
            input.input.classesToUse = modelUserPreferences?.classesToUse;
          }
        } else {
          input.input.classesToUse = orchestrationData?.model?.classes;
        }

        const relevantModelsByType = modelTypesByType?.[inputModelType];
        const stainingMethod = getStainingMethodByStainType(slideStain);
        const matchingModelType =
          find(relevantModelsByType, (modelTypeOption) => modelTypeOption.stainingMethod === stainingMethod) ||
          find(relevantModelsByType, (modelTypeOption) => !modelTypeOption.stainingMethod);

        if (matchingModelType?.backgroundClassesToIgnore) {
          input.input.classesToUse = difference(input.input.classesToUse, matchingModelType?.backgroundClassesToIgnore);
        }

        if (
          includes(
            map(classificationModelOptions, (option) => option.id),
            modelType
          )
        ) {
          input.input.readAs = modelType;

          // if the model is classification intensity model, add binning params
          if (find(classificationModelOptions, { id: modelType })?.needBinningParams) {
            if (modelUserPreferences?.numBins) {
              input.input.binning = { numBins: modelUserPreferences?.numBins };
            } else if (modelUserPreferences?.mapValuesToBins) {
              input.input.binning = { mapValuesToBins: modelUserPreferences?.mapValuesToBins };
            } else if (modelPreferencesByModelId[modelId]?.numBins) {
              input.input.binning = { numBins: modelPreferencesByModelId[modelId].numBins };
            } else if (modelPreferencesByModelId[modelId]?.mapValuesToBins) {
              input.input.binning = { mapValuesToBins: modelPreferencesByModelId[modelId].mapValuesToBins };
            }
          }
        }

        inputsPerCasePerSlide[caseId][slideId].push(input);

        // All inference results must have the same TSM URL.
        // Therefore, it doesn't matter which TSM URL is selected (from area, cell, etc.) since they are all identical.
        // Hence, I will use the first one I find.
        if (
          isEmpty(modelTsmArtifactUrl) &&
          (!isEmpty(orchestrationData?.orchestration?.orchestrationTsmArtifactUrlPattern) ||
            !isEmpty(orchestrationData?.orchestration?.tsmSlideOverrides?.[slideId]))
        ) {
          modelTsmArtifactUrl =
            orchestrationData?.orchestration?.tsmSlideOverrides?.[slideId] ??
            orchestrationData?.orchestration?.orchestrationTsmArtifactUrlPattern;
        }
      });

      // Add tsm model artifact url
      if (!isEmpty(modelTsmArtifactUrl)) {
        inputsPerCasePerSlide[caseId][slideId].push({
          input: {
            artifactUrl: modelTsmArtifactUrl,
            modelType: modelTypeTsm.apiModelValue,
          },
        });
      }

      // Add selected non model orchestrations
      // postprocessed artifacts
      // pips artifacts
      // cell quantification artifacts
      forEach(allSelectedNonModelOrchestrations, (nonModelOrchestrationsByResultName) => {
        forEach(nonModelOrchestrationsByResultName[slideId], (orchestrationData, resultKey) => {
          if (orchestrationData?.orchestration?.params?.data) {
            const input: ResultsInput = {
              input: {
                artifactUrl: orchestrationData?.orchestration?.orchestrationResultArtifactUrlPattern,
              },
            };

            const modelUserPreferences = modelPreferencesByModelId?.[resultKey];

            // if the model have binning params and is not a classification model
            if (
              modelUserPreferences &&
              isEmpty(modelUserPreferences?.readAs) &&
              (modelUserPreferences?.numBins || modelUserPreferences?.mapValuesToBins)
            ) {
              if (modelUserPreferences?.numBins) {
                input.input.binning = { numBins: modelUserPreferences?.numBins };
              } else if (modelUserPreferences?.mapValuesToBins) {
                input.input.binning = { mapValuesToBins: modelUserPreferences?.mapValuesToBins };
              }
            }

            if (orchestrationData?.orchestration?.params?.resultType === ResultType.SlideDetectionsTable) {
              // The declared panel for the stain (every probe named across every selected detections
              // orchestration), not just whichever orchestration this one slide happens to use - a
              // probe absent from this slide's own artifact must still get general or override bins.
              const probeNames = getSpotDetectionProbeNamesForStain(orchestrationMetadata, slideStain);
              // Per-probe H-score bin overrides live under their own probe-keyed preference entry
              // (see getSpotDetectionThresholdOptions and friends in usePostProcessingActions.tsx).
              const perProbeCountBins: Record<string, SpotDetectionCountBin[]> = {};
              forEach(probeNames, (probeName) => {
                const probeCountBins = modelPreferencesByModelId?.[probeName]?.countBins;
                if (!isEmpty(probeCountBins)) {
                  perProbeCountBins[probeName] = probeCountBins;
                }
              });
              applySpotDetectionSetupToInput(input.input, modelUserPreferences, probeNames, perProbeCountBins);
            }

            inputsPerCasePerSlide[caseId][slideId].push(input);
          }
        });
      });

      // Add selected assignments
      forEach(selectedAssignmentsValues, (selectedAssignment) => {
        if (includes(selectedAssignment.assignment?.slideIds, slideId)) {
          const assignmentInput: Input = {
            assignmentId: selectedAssignment.assignment?.annotationAssignmentId,
            modelType: selectedAssignment.modelType,
          };

          if (selectedAssignment.useClassesFromIgnore) {
            assignmentInput.classesToIgnore = selectedAssignment.classesToIgnore;
          } else {
            assignmentInput.classesToUse = selectedAssignment.classesToUse;
          }

          const inputByModelTypeIndex = findIndex(
            inputsPerCasePerSlide[caseId][slideId],
            (input) => input.input.modelType === assignmentInput.modelType
          );

          if (assignmentInput.modelType && inputByModelTypeIndex !== -1) {
            inputsPerCasePerSlide[caseId][slideId][inputByModelTypeIndex].override = assignmentInput;
          } else {
            inputsPerCasePerSlide[caseId][slideId].push({ input: assignmentInput });
          }
        }
      });
    });
  });

  return inputsPerCasePerSlide;
};
